import TailwindAnimate from "tailwindcss-animate";
import TailwindScrollbar from "tailwind-scrollbar";
import TailwindContainerQueries from "@tailwindcss/container-queries";
const tailwindDesignSystemTheme = {
  theme: {
    container: false,
    extend: {
      colors: {
        background: "var(--background)",
        foreground: "var(--foreground)",
        card: {
          DEFAULT: "hsl(var(--card))",
          foreground: "hsl(var(--card-foreground))"
        },
        popover: {
          DEFAULT: "hsl(var(--popover))",
          foreground: "hsl(var(--popover-foreground))"
        },
        primary: {
          DEFAULT: "var(--primary)",
          foreground: "var(--primary-foreground)"
        },
        secondary: {
          DEFAULT: "var(--secondary)",
          foreground: "var(--secondary-foreground)"
        },
        muted: {
          DEFAULT: "var(--muted)",
          foreground: "var(--muted-foreground)"
        },
        accent: {
          DEFAULT: "var(--accent)",
          foreground: "var(--accent-foreground)"
        },
        destructive: {
          DEFAULT: "var(--destructive)",
          foreground: "var(--destructive-foreground)"
        },
        border: "var(--border)",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        chart: {
          1: "hsl(var(--chart-1))",
          2: "hsl(var(--chart-2))",
          3: "hsl(var(--chart-3))",
          4: "hsl(var(--chart-4))",
          5: "hsl(var(--chart-5))"
        },
        blue: {
          DEFAULT: "#0088eb",
          50: "#f7fbfe",
          100: "#F1F9FF",
          200: "#dbeefc",
          300: "#b0daf9",
          400: "#54aff2",
          500: "#33a0ef",
          600: "#0088eb",
          700: "#007cd6",
          800: "#0061a7",
          900: "#004b81"
        },
        green: {
          DEFAULT: "#1db97d",
          50: "#e9faf3",
          100: "#baefda",
          200: "#98e7c9",
          300: "#6adcb0",
          400: "#4dd5a1",
          500: "#20cb89",
          600: "#1db97d",
          700: "#179061",
          800: "#12704b",
          900: "#0d553a"
        },
        slate: {
          DEFAULT: "#475569",
          50: "#F8FAFC",
          100: "#F1F5F9",
          200: "#E2E8F0",
          300: "#CBD5E1",
          400: "#94A3B8",
          500: "#64748B",
          600: "#475569",
          700: "#334155",
          800: "#1E293B",
          900: "#151515"
        },
        warmGreen: {
          DEFAULT: "#6da73b",
          50: "#f2f8ec",
          100: "#d5e9c4",
          200: "#c1dea8",
          300: "#a5cf80",
          400: "#93c667",
          500: "#78b841",
          600: "#6da73b",
          700: "#55832e",
          800: "#426524",
          900: "#324d1b"
        },
        orange: {
          DEFAULT: "#e6a53b",
          50: "#fff8ec",
          100: "#fee8c4",
          200: "#fedda8",
          300: "#fecd80",
          400: "#fdc467",
          500: "#fdb541",
          600: "#e6a53b",
          700: "#b4812e",
          800: "#8b6424",
          900: "#6a4c1b"
        },
        red: {
          DEFAULT: "#ef4444",
          50: "#fef2f2",
          100: "#fee2e2",
          200: "#fecaca",
          300: "#fca5a5",
          400: "#f87171",
          500: "#ef4444",
          600: "#dc2626",
          700: "#b91c1c",
          800: "#991b1b",
          900: "#7f1d1d"
        },
        lightTextSub: "var(--color-lightTextSub)",
        defaultText: "var(--color-defaultText)"
      },
      borderRadius: {
        DEFAULT: "12px",
        none: "0px",
        xs: "4px",
        xs_1px: "3px",
        sm: "8px",
        sm_1px: "7px",
        md: "10px",
        md_1px: "9px",
        lg: "12px",
        lg_1px: "11px",
        xl: "16px",
        xl_1px: "15px",
        "2xl": "24px",
        "3xl": "32px",
        "4xl": "40px",
        full: "9999px"
      },
      screens: {
        sm: "575px",
        md: "768px",
        lg: "992px",
        xl: "1280px",
        "2xl": "1400px"
      },
      containers: {
        "@xs": "320px",
        "@sm": "384px",
        "@md": "448px",
        "@lg": "512px",
        "@xl": "575px",
        "@2xl": "672px",
        "@3xl": "768px",
        "@4xl": "992px",
        "@5xl": "1024px",
        "@6xl": "1152px",
        "@7xl": "1280px"
      },
      fontWeight: {
        normal: "400",
        medium: "500",
        semibold: "600",
        bold: "700",
        extrabold: "800"
      },
      fontSize: {
        d1: [
          "var(--d1-font-size)",
          {
            lineHeight: "var(--d1-line-height)",
            letterSpacing: "var(--d1-letter-spacing)",
            fontWeight: "800"
          }
        ],
        d2: [
          "var(--d2-font-size)",
          {
            lineHeight: "var(--d2-line-height)",
            letterSpacing: "var(--d2-letter-spacing)",
            fontWeight: "800"
          }
        ],
        h1: [
          "var(--h1-font-size)",
          { lineHeight: "var(--h1-line-height)", fontWeight: "700" }
        ],
        h2: [
          "var(--h2-font-size)",
          { lineHeight: "var(--h2-line-height)", fontWeight: "700" }
        ],
        h3: [
          "var(--h3-font-size)",
          { lineHeight: "var(--h3-line-height)", fontWeight: "700" }
        ],
        h4: [
          "var(--h4-font-size)",
          { lineHeight: "var(--h4-line-height)", fontWeight: "400" }
        ],
        t1: [
          "var(--t1-font-size)",
          { lineHeight: "var(--t1-line-height)", fontWeight: "700" }
        ],
        t2: [
          "var(--t2-font-size)",
          { lineHeight: "var(--t2-line-height)", fontWeight: "700" }
        ],
        t3: [
          "var(--t3-font-size)",
          { lineHeight: "var(--t3-line-height)", fontWeight: "700" }
        ],
        t4: [
          "var(--t4-font-size)",
          { lineHeight: "var(--t4-line-height)", fontWeight: "700" }
        ],
        s1: [
          "var(--s1-font-size)",
          {
            lineHeight: "var(--s1-line-height)",
            fontWeight: "var(--s1-font-weight)"
          }
        ],
        s2: [
          "var(--s2-font-size)",
          {
            lineHeight: "var(--s2-line-height)",
            fontWeight: "var(--s2-font-weight)"
          }
        ],
        s3: [
          "var(--s3-font-size)",
          {
            lineHeight: "var(--s3-line-height)",
            fontWeight: "var(--s3-font-weight)"
          }
        ],
        b1: [
          "var(--b1-font-size)",
          { lineHeight: "var(--b1-line-height)", fontWeight: "400" }
        ],
        b2: [
          "var(--b2-font-size)",
          { lineHeight: "var(--b2-line-height)", fontWeight: "400" }
        ],
        b3: [
          "var(--b3-font-size)",
          { lineHeight: "var(--b3-line-height)", fontWeight: "400" }
        ],
        c1: [
          "var(--c1-font-size)",
          { lineHeight: "var(--c1-line-height)", fontWeight: "400" }
        ],
        c2: [
          "var(--c2-font-size)",
          { lineHeight: "var(--c2-line-height)", fontWeight: "400" }
        ]
      },
      letterSpacing: {
        tighter: "-0.03em",
        tight: "-0.01em",
        normal: "0",
        wide: "0.03em"
      },
      backgroundImage: {
        "gradient-main": "linear-gradient(144.72deg, #149ADE -7.43%, #0EB158 118.33%)",
        "gradient-main-light": "linear-gradient(152.03deg, #E8F5FC -3.47%, #E7F7EE 101.39%)",
        "gradient-secondary": "linear-gradient(117.99deg, #FFD5C2 13.44%, #B2A6FF 116%)",
        "gradient-secondary-light": "linear-gradient(118deg, #FFFCFB 13.44%, #FAF9FF 116%)",
        "gradient-golden": "linear-gradient(116.75deg, #FFC874 9.35%, #FFEB37 104.57%)",
        "gradient-golden-light": "linear-gradient(116.75deg, #FFFBF5 9.35%, #FFFEF1 104.57%)",
        "gradient-warning": "linear-gradient(90deg, #FBECC5 0%, #FDE2E2 100%)",
        "gradient-light-blue": "linear-gradient(151.65deg, #E0F1FD 9.99%, #D6F3FF 51.15%, #E4F9F1 91.66%)",
        "gradient-light-blue-green": "linear-gradient(152deg, #E8F5FC -3.47%, #E7F7EE 101.39%)"
      },
      boxShadow: {
        xs: "var(--shadow-xs)",
        sm: "var(--shadow-sm)",
        md: "var(--shadow-md)",
        lg: "var(--shadow-lg)",
        xl: "var(--shadow-xl)"
      },
      dropShadow: {
        xs: "0px 2px 4px rgba(15, 23, 42, 0.08)",
        sm: "0px 4px 8px rgba(15, 23, 42, 0.1)",
        md: "0px 8px 16px rgba(15, 23, 42, 0.1)",
        lg: "0px 12px 24px rgba(15, 23, 42, 0.15)",
        xl: "0px 16px 32px rgba(15, 23, 42, 0.2)"
      },
      spacing: {
        4.5: "1.125rem",
        xs: "0.25rem",
        sm: "0.5rem",
        md: "0.625rem",
        lg: "0.75rem",
        xl: "1rem",
        "2xl": "1.5rem",
        "3xl": "2rem",
        "4xl": "2.5rem",
        "5xl": "3.5rem"
      },
      padding: {
        "fluid-sm": "16px",
        // Padding for small screens (sm)
        "fluid-md": "16px",
        // Padding for medium screens (md)
        "fluid-lg": "68px",
        // Padding for large screens (lg)
        "fluid-xl": "68px",
        // Padding for extra-large screens (xl)
        "fluid-2xl": "68px"
        // Padding for 2x large screens (2xl)
      },
      keyframes: {
        float: {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-10px)" }
        }
      },
      animation: {
        float: "float 3s ease-in-out infinite"
      }
    }
  },
  plugins: [
    TailwindAnimate,
    TailwindScrollbar,
    TailwindContainerQueries,
    function({ addComponents }) {
      addComponents({
        ".avoid-container-padding": {
          marginLeft: "-16px",
          // Centering the container for smaller screens
          marginRight: "-16px"
          // Centering the container for smaller screens
        },
        ".avoid-container-padding-start": {
          marginInlineStart: "-16px"
          // Centering the container start
        },
        ".avoid-container-padding-end": {
          marginInlineEnd: "-16px"
          // Centering the container end
        },
        ".center": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
        ".rtl": {
          direction: "rtl"
        },
        "@screen sm": {
          ".avoid-container-padding": {
            marginLeft: "-16px",
            // Centering the container
            marginRight: "-16px"
            // Centering the container
          },
          ".avoid-container-padding-start": {
            marginInlineStart: "-16px"
            // Centering the container start
          },
          ".avoid-container-padding-end": {
            marginInlineEnd: "-16px"
            // Centering the container end
          }
        },
        "@screen md": {
          ".avoid-container-padding": {
            marginLeft: "-16px",
            // Centering the container
            marginRight: "-16px"
            // Centering the container
          },
          ".avoid-container-padding-start": {
            marginInlineStart: "-16px"
            // Centering the container start
          },
          ".avoid-container-padding-end": {
            marginInlineEnd: "-16px"
            // Centering the container end
          }
        },
        "@screen lg": {
          ".avoid-container-padding": {
            marginLeft: "-68px",
            // Centering the container
            marginRight: "-68px"
            // Centering the container
          },
          ".avoid-container-padding-start": {
            marginInlineStart: "-68px"
            // Centering the container start
          },
          ".avoid-container-padding-end": {
            marginInlineEnd: "-68px"
            // Centering the container end
          }
        },
        "@screen xl": {
          ".avoid-container-padding": {
            marginLeft: "-68px",
            // Centering the container
            marginRight: "-68px"
            // Centering the container
          },
          ".avoid-container-padding-start": {
            marginInlineStart: "-68px"
            // Centering the container start
          },
          ".avoid-container-padding-end": {
            marginInlineEnd: "-68px"
            // Centering the container end
          }
        },
        "@screen 2xl": {
          ".avoid-container-padding": {
            marginLeft: "-68px",
            // Centering the container
            marginRight: "-68px"
            // Centering the container
          },
          ".avoid-container-padding-start": {
            marginInlineStart: "-68px"
            // Centering the container start
          },
          ".avoid-container-padding-end": {
            marginInlineEnd: "-68px"
            // Centering the container end
          }
        }
      });
    }
  ]
};
export {
  tailwindDesignSystemTheme
};
